import { useState } from "react";
import { Plus, Pencil, Trash2, X, Save, ExternalLink, ImagePlus } from "lucide-react";
import ImageUpload from "@/components/ImageUpload";

/**
 * Normalize a `products` value coming from the API into the new shape.
 * Backwards compatible: a plain string becomes `{ name: str, description: "", image: "" }`.
 */
export function normalizeProducts(raw) {
  if (!Array.isArray(raw)) return [];
  return raw.map((p) => {
    if (typeof p === "string") return { name: p, description: "", image: "", url: "" };
    return {
      name: p?.name || "",
      description: p?.description || "",
      image: p?.image || "",
      url: p?.url || "",
    };
  }).filter((p) => p.name);
}

/**
 * Editable list of products. Each product is { name, description, image, url }.
 * Props:
 *  - value: Product[]
 *  - onChange: (next) => void
 *  - testId: string
 */
export default function ProductEditor({ value, onChange, testId = "product-editor" }) {
  const products = normalizeProducts(value);
  const [editing, setEditing] = useState(null); // { index, product } or { index: -1, product: {...} } for new

  const startNew = () => setEditing({ index: -1, product: { name: "", description: "", image: "", url: "" } });
  const startEdit = (i) => setEditing({ index: i, product: { ...products[i] } });

  const save = () => {
    if (!editing.product.name.trim()) return;
    const next = [...products];
    if (editing.index === -1) next.push(editing.product);
    else next[editing.index] = editing.product;
    onChange(next);
    setEditing(null);
  };
  const remove = (i) => {
    const next = products.filter((_, idx) => idx !== i);
    onChange(next);
  };

  return (
    <div data-testid={testId}>
      <div className="grid gap-2 sm:grid-cols-2">
        {products.map((p, i) => (
          <div key={i} className="group relative flex items-center gap-3 rounded-xl border border-neutral-200 bg-white p-2.5" data-testid={`${testId}-item-${i}`}>
            <ProductThumb image={p.image} />
            <div className="min-w-0 flex-1">
              <div className="truncate text-sm font-bold text-neutral-900">{p.name}</div>
              {p.description && <div className="line-clamp-1 text-xs text-neutral-500">{p.description}</div>}
            </div>
            <div className="flex shrink-0 gap-1 opacity-60 group-hover:opacity-100">
              <button type="button" onClick={() => startEdit(i)} className="grid h-7 w-7 place-items-center rounded-full text-neutral-600 hover:bg-wine-50 hover:text-wine-700" aria-label="Modifier" data-testid={`${testId}-edit-${i}`}>
                <Pencil className="h-3.5 w-3.5" />
              </button>
              <button type="button" onClick={() => remove(i)} className="grid h-7 w-7 place-items-center rounded-full text-neutral-600 hover:bg-rose-50 hover:text-rose-700" aria-label="Supprimer" data-testid={`${testId}-remove-${i}`}>
                <Trash2 className="h-3.5 w-3.5" />
              </button>
            </div>
          </div>
        ))}
        <button type="button" onClick={startNew} className="flex items-center justify-center gap-1.5 rounded-xl border-2 border-dashed border-wine-200 bg-wine-50/20 px-3 py-3 text-xs font-bold text-wine-700 hover:bg-wine-50" data-testid={`${testId}-add`}>
          <Plus className="h-4 w-4" /> Ajouter un produit / service
        </button>
      </div>

      {editing && (
        <ProductEditDialog
          product={editing.product}
          onChange={(prod) => setEditing({ ...editing, product: prod })}
          onCancel={() => setEditing(null)}
          onSave={save}
          isNew={editing.index === -1}
          testId={`${testId}-dialog`}
        />
      )}
    </div>
  );
}

function ProductThumb({ image }) {
  const BACKEND = process.env.REACT_APP_BACKEND_URL;
  const fullUrl = image && image.startsWith("/api/") ? `${BACKEND}${image}` : image;
  if (!fullUrl) {
    return (
      <div className="grid h-12 w-12 shrink-0 place-items-center rounded-lg bg-wine-50 text-wine-300">
        <ImagePlus className="h-5 w-5" />
      </div>
    );
  }
  return <img src={fullUrl} alt="" className="h-12 w-12 shrink-0 rounded-lg object-cover" />;
}

function ProductEditDialog({ product, onChange, onSave, onCancel, isNew, testId }) {
  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center p-4" data-testid={testId}>
      <button onClick={onCancel} className="absolute inset-0 bg-neutral-900/70 backdrop-blur-sm" aria-label="Fermer" />
      <div className="relative w-full max-w-lg overflow-hidden rounded-2xl bg-white shadow-2xl">
        <div className="flex items-center justify-between border-b border-neutral-100 px-5 py-3">
          <h3 className="text-base font-bold text-neutral-900">{isNew ? "Nouveau produit / service" : "Modifier le produit"}</h3>
          <button onClick={onCancel} className="grid h-8 w-8 place-items-center rounded-full text-neutral-500 hover:bg-neutral-100" aria-label="Fermer"><X className="h-4 w-4" /></button>
        </div>
        <div className="space-y-3 p-5">
          <div>
            <label className="text-[10px] font-bold uppercase tracking-wider text-neutral-500">Nom *</label>
            <input
              autoFocus
              value={product.name}
              onChange={(e) => onChange({ ...product, name: e.target.value })}
              placeholder="ex. Plateforme IoT Vigne"
              className="mt-1 w-full rounded-xl border border-neutral-200 px-3 py-2 text-sm"
              data-testid={`${testId}-name`}
            />
          </div>
          <div>
            <label className="text-[10px] font-bold uppercase tracking-wider text-neutral-500">Description</label>
            <textarea
              rows={3}
              value={product.description}
              onChange={(e) => onChange({ ...product, description: e.target.value })}
              placeholder="Décrivez votre produit/service en quelques lignes…"
              className="mt-1 w-full rounded-xl border border-neutral-200 px-3 py-2 text-sm"
              data-testid={`${testId}-description`}
            />
          </div>
          <div>
            <label className="text-[10px] font-bold uppercase tracking-wider text-neutral-500">Lien externe (optionnel)</label>
            <input
              value={product.url}
              onChange={(e) => onChange({ ...product, url: e.target.value })}
              placeholder="https://…"
              className="mt-1 w-full rounded-xl border border-neutral-200 px-3 py-2 text-sm"
              data-testid={`${testId}-url`}
            />
          </div>
          <ImageUpload
            value={product.image}
            onChange={(url) => onChange({ ...product, image: url })}
            label="Visuel (16:9 recommandé)"
            testId={`${testId}-image`}
          />
        </div>
        <div className="flex justify-end gap-2 border-t border-neutral-100 px-5 py-3">
          <button onClick={onCancel} className="rounded-full bg-neutral-100 px-5 py-2 text-xs font-semibold text-neutral-700 hover:bg-neutral-200" data-testid={`${testId}-cancel`}>Annuler</button>
          <button onClick={onSave} disabled={!product.name.trim()} className="inline-flex items-center gap-2 rounded-full bg-wine-600 px-5 py-2 text-xs font-bold text-white hover:bg-wine-700 disabled:opacity-50" data-testid={`${testId}-save`}>
            <Save className="h-3.5 w-3.5" /> {isNew ? "Ajouter" : "Enregistrer"}
          </button>
        </div>
      </div>
    </div>
  );
}

// =========================================================================
// Display (read-only) — clickable product cards opening a detail modal
// =========================================================================

/**
 * Grid of product cards. Each card is clickable and opens a details modal.
 * Props:
 *  - value: Product[] (raw — will be normalized)
 *  - testId: string
 */
export function ProductGallery({ value, testId = "product-gallery" }) {
  const products = normalizeProducts(value);
  const [open, setOpen] = useState(null);
  if (products.length === 0) return null;
  return (
    <div data-testid={testId}>
      <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
        {products.map((p, i) => (
          <ProductCard key={i} product={p} onOpen={() => setOpen(p)} testId={`${testId}-card-${i}`} />
        ))}
      </div>
      {open && <ProductDetailsModal product={open} onClose={() => setOpen(null)} />}
    </div>
  );
}

function ProductCard({ product, onOpen, testId }) {
  const BACKEND = process.env.REACT_APP_BACKEND_URL;
  const fullUrl = product.image && product.image.startsWith("/api/") ? `${BACKEND}${product.image}` : product.image;
  return (
    <button
      type="button"
      onClick={onOpen}
      className="group flex flex-col overflow-hidden rounded-2xl border border-neutral-100 bg-white text-left shadow-sm transition hover:-translate-y-0.5 hover:shadow-lg hover:border-wine-300"
      data-testid={testId}
    >
      {fullUrl ? (
        <img src={fullUrl} alt={product.name} className="aspect-[16/9] w-full object-cover" />
      ) : (
        <div className="grid aspect-[16/9] w-full place-items-center bg-gradient-to-br from-wine-50 to-rose-50 text-wine-300">
          <ImagePlus className="h-10 w-10" />
        </div>
      )}
      <div className="flex flex-1 flex-col p-3">
        <div className="font-bold text-neutral-900 line-clamp-1">{product.name}</div>
        {product.description && <p className="mt-1 line-clamp-2 text-xs text-neutral-600">{product.description}</p>}
        <div className="mt-auto pt-2 text-[10px] font-bold uppercase tracking-wider text-wine-600 group-hover:text-wine-700">Voir le détail →</div>
      </div>
    </button>
  );
}

function ProductDetailsModal({ product, onClose }) {
  const BACKEND = process.env.REACT_APP_BACKEND_URL;
  const fullUrl = product.image && product.image.startsWith("/api/") ? `${BACKEND}${product.image}` : product.image;
  return (
    <div className="fixed inset-0 z-[70] flex items-center justify-center p-4" data-testid="product-details-modal">
      <button onClick={onClose} className="absolute inset-0 bg-neutral-900/70 backdrop-blur-sm" aria-label="Fermer" />
      <div className="relative w-full max-w-2xl overflow-hidden rounded-3xl bg-white shadow-2xl">
        {fullUrl && (
          <img src={fullUrl} alt={product.name} className="aspect-[16/7] w-full object-cover" />
        )}
        <button onClick={onClose} className="absolute right-3 top-3 grid h-9 w-9 place-items-center rounded-full bg-white/90 text-neutral-700 hover:bg-white" aria-label="Fermer">
          <X className="h-5 w-5" />
        </button>
        <div className="p-6">
          <h2 className="text-2xl font-bold text-neutral-900">{product.name}</h2>
          {product.description && (
            <p className="mt-3 whitespace-pre-line text-sm leading-relaxed text-neutral-700">{product.description}</p>
          )}
          {product.url && (
            <a
              href={product.url.startsWith("http") ? product.url : `https://${product.url}`}
              target="_blank"
              rel="noreferrer"
              className="mt-5 inline-flex items-center gap-2 rounded-full bg-wine-600 px-5 py-2.5 text-sm font-bold text-white hover:bg-wine-700"
              data-testid="product-details-link"
            >
              En savoir plus <ExternalLink className="h-3.5 w-3.5" />
            </a>
          )}
        </div>
      </div>
    </div>
  );
}
